import { html, nothing } from "lit";
import type { FleetFilesTreeNode } from "../chat/fleet-session-files-tree.ts";

export type FleetSessionFilesPanelProps = {
  loading: boolean;
  error: string | null;
  workspacePath: string | null;
  scope: "sandbox" | "session" | null;
  truncated: boolean;
  tree: FleetFilesTreeNode[];
  expandedDirs: Record<string, boolean>;
  selectedPath: string | null;
  fileLoading: boolean;
  fileError: string | null;
  fileContent: string | null;
  fileTruncated: boolean;
  connected: boolean;
  onRefresh: () => void;
  onToggleDir: (path: string) => void;
  onOpenFile: (path: string) => void;
  onInsertIntoComposer?: (text: string) => void;
};

type FlatRow = {
  node: FleetFilesTreeNode;
  depth: number;
};

function flattenTree(
  nodes: FleetFilesTreeNode[],
  expandedDirs: Record<string, boolean>,
  depth = 0,
): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const node of nodes) {
    rows.push({ node, depth });
    if (node.kind === "dir" && expandedDirs[node.path] && node.children?.length) {
      rows.push(...flattenTree(node.children, expandedDirs, depth + 1));
    }
  }
  return rows;
}

function countFiles(nodes: FleetFilesTreeNode[]): number {
  let n = 0;
  for (const node of nodes) {
    if (node.kind === "file") {
      n += 1;
    } else if (node.children) {
      n += countFiles(node.children);
    }
  }
  return n;
}

export function renderFleetSessionFilesPanel(props: FleetSessionFilesPanelProps) {
  const rows = flattenTree(props.tree, props.expandedDirs);
  const fileCount = countFiles(props.tree);
  const scopeLabel = props.scope === "sandbox" ? "sandbox" : "session";
  const meta = props.loading
    ? "Loading…"
    : `${scopeLabel} · ${fileCount} file${fileCount === 1 ? "" : "s"}${props.truncated ? " · truncated" : ""}`;

  return html`
    <div class="fleet-session-files">
      <div class="fleet-session-files__toolbar">
        <div class="fleet-session-files__toolbar-text">
          <div class="card-title">Project files</div>
          <div class="card-sub muted">
            Root workspace, per-project folders, chat-only files, and each subagent session.
            ${meta}
          </div>
          ${
            props.workspacePath
              ? html`
                  <div class="mono muted fleet-session-files__path" title=${props.workspacePath}>
                    ${props.workspacePath}
                  </div>
                `
              : nothing
          }
        </div>
        <button
          type="button"
          class="btn btn--sm"
          ?disabled=${!props.connected || props.loading}
          @click=${props.onRefresh}
        >
          Refresh
        </button>
      </div>

      ${
        props.error
          ? html`<div class="callout danger fleet-session-files__banner">${props.error}</div>`
          : nothing
      }

      <div class="fleet-session-files__split">
        <div class="fleet-session-files__tree-pane" role="region" aria-label="File tree">
          ${
            props.loading
              ? html`
                  <p class="muted fleet-session-files__hint">Scanning workspaces…</p>
                `
              : rows.length === 0
                ? html`
                    <p class="muted fleet-session-files__hint">
                      No files yet. Agents can write with tools even without creating a project.
                    </p>
                  `
                : html`
                    <div class="fleet-session-files__tree" role="tree">
                      ${rows.map(({ node, depth }) => renderTreeRow(node, depth, props))}
                    </div>
                  `
          }
        </div>
        <div class="fleet-session-files__preview-pane" role="region" aria-label="File preview">
          ${renderPreview(props)}
        </div>
      </div>
    </div>
  `;
}

function renderTreeRow(
  node: FleetFilesTreeNode,
  depth: number,
  props: FleetSessionFilesPanelProps,
) {
  const isDir = node.kind === "dir";
  const expanded = Boolean(props.expandedDirs[node.path]);
  const selected = props.selectedPath === node.path;
  const icon = isDir ? (expanded ? "▼" : "▶") : "📄";
  const glyph = isDir ? "📁" : "";

  return html`
    <div
      class="fleet-session-files__row ${selected ? "fleet-session-files__row--selected" : ""}"
      role="treeitem"
      style=${`--depth: ${depth}`}
    >
      <button
        type="button"
        class="fleet-session-files__row-btn"
        @click=${() => (isDir ? props.onToggleDir(node.path) : props.onOpenFile(node.path))}
      >
        <span class="fleet-session-files__chevron" aria-hidden="true">${isDir ? icon : ""}</span>
        <span class="fleet-session-files__glyph" aria-hidden="true">${glyph}</span>
        <span class="fleet-session-files__label">${node.name}</span>
      </button>
    </div>
  `;
}

function renderPreview(props: FleetSessionFilesPanelProps) {
  if (!props.selectedPath) {
    return html`
      <p class="muted fleet-session-files__preview-empty">Select a file to preview its contents.</p>
    `;
  }

  return html`
    <div class="fleet-session-files__preview">
      <div class="fleet-session-files__preview-header">
        <span class="mono muted">${props.selectedPath.replace(/^fleet:\//, "")}</span>
        ${
          props.fileTruncated
            ? html`
                <span class="muted"> · preview truncated</span>
              `
            : nothing
        }
        ${
          props.onInsertIntoComposer
            ? html`
                <span class="fleet-session-files__preview-actions">
                  <button
                    type="button"
                    class="btn btn--sm"
                    @click=${() => props.onInsertIntoComposer?.(`@${props.selectedPath}`)}
                  >
                    Insert path
                  </button>
                  <button
                    type="button"
                    class="btn btn--sm"
                    ?disabled=${!props.fileContent}
                    @click=${() => {
                      const body = props.fileContent?.trim() ?? "";
                      const fence = body.includes("```") ? "````" : "```";
                      props.onInsertIntoComposer?.(
                        `File: ${props.selectedPath}\n${fence}\n${body}\n${fence}\n`,
                      );
                    }}
                  >
                    Insert snippet
                  </button>
                </span>
              `
            : nothing
        }
      </div>
      ${
        props.fileLoading
          ? html`
              <p class="muted fleet-session-files__preview-empty">Loading file…</p>
            `
          : props.fileError
            ? html`<div class="callout danger">${props.fileError}</div>`
            : html`
                <pre class="fleet-session-files__preview-content mono">${props.fileContent ?? ""}</pre>
              `
      }
    </div>
  `;
}
